import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaShoppingCart, FaTrash, FaPlus, FaMinus, FaClipboardList, FaArrowRight, FaExternalLinkAlt } from 'react-icons/fa';
import axios from 'axios';

const WarenkorbPage = () => {
  const [warenkorb, setWarenkorb] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [bestellungInProgress, setBestellungInProgress] = useState(false);

  // Warenkorb laden
  const loadWarenkorb = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/warenkorb');
      setWarenkorb(response.data);
      
    } catch (err) {
      console.error('Fehler beim Laden des Warenkorbs:', err);
      setError('Der Warenkorb konnte nicht geladen werden. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Beim ersten Rendern Warenkorb laden
  useEffect(() => {
    loadWarenkorb();
  }, []);

  // Menge eines Artikels ändern
  const updateMenge = async (id, neueMenge) => {
    try {
      await axios.put(`/api/warenkorb/${id}`, { menge: neueMenge });
      await loadWarenkorb(); // Warenkorb neu laden
    } catch (err) {
      console.error('Fehler beim Aktualisieren der Menge:', err);
      setError('Die Menge konnte nicht aktualisiert werden.');
    }
  };

  // Artikel aus dem Warenkorb entfernen
  const removeItem = async (id) => {
    try {
      await axios.delete(`/api/warenkorb/${id}`);
      await loadWarenkorb(); // Warenkorb neu laden
    } catch (err) {
      console.error('Fehler beim Entfernen des Artikels:', err);
      setError('Der Artikel konnte nicht entfernt werden.');
    }
  };

  // Warenkorb leeren
  const clearWarenkorb = async () => {
    if (window.confirm('Möchten Sie wirklich den gesamten Warenkorb leeren?')) {
      try {
        await axios.delete('/api/warenkorb');
        await loadWarenkorb(); // Warenkorb neu laden
      } catch (err) {
        console.error('Fehler beim Leeren des Warenkorbs:', err);
        setError('Der Warenkorb konnte nicht geleert werden.');
      }
    }
  };

  // Bestellung aus dem Warenkorb erstellen
  const createBestellung = async () => {
    if (warenkorb.length === 0) {
      setError('Es kann keine Bestellung aus einem leeren Warenkorb erstellt werden.');
      return;
    }

    try {
      setBestellungInProgress(true);
      await axios.post('/api/bestellungen', { ausWarenkorb: true });
      setOrderSuccess(true);
      await loadWarenkorb(); // Warenkorb wird automatisch geleert und neu geladen
    } catch (err) {
      console.error('Fehler beim Erstellen der Bestellung:', err);
      setError('Die Bestellung konnte nicht erstellt werden.');
    } finally {
      setBestellungInProgress(false);
    }
  };

  return (
    <Container>
      <h1 className="mb-4">Warenkorb</h1>
      
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {orderSuccess && (
        <Alert variant="success" dismissible onClose={() => setOrderSuccess(false)}>
          <Alert.Heading>Bestellung erfolgreich erstellt!</Alert.Heading>
          <p>Ihre Bestellung wurde erfolgreich erstellt und der Warenkorb wurde geleert.</p>
          <div className="d-flex justify-content-end">
            <Button 
              as={Link} 
              to="/bestellungen" 
              variant="outline-success"
            >
              Zu meinen Bestellungen <FaArrowRight className="ms-2" />
            </Button>
          </div>
        </Alert>
      )}
      
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Laden...</span>
          </Spinner>
          <p className="mt-2">Warenkorb wird geladen...</p>
        </div>
      ) : warenkorb.length === 0 ? (
        <Card className="text-center p-5 shadow-sm">
          <Card.Body>
            <FaShoppingCart size={50} className="text-muted mb-3" />
            <h3>Ihr Warenkorb ist leer</h3>
            <p className="text-muted mb-4">Fügen Sie Artikel zu Ihrem Warenkorb hinzu, um eine Bestellung vorzubereiten.</p>
            <Button as={Link} to="/artikel" variant="primary">
              Zu den Artikeln
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm mb-4">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Artikel</th>
                    <th className="text-center">Menge</th>
                    <th>Kategorie</th>
                    <th>Online-Shop</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {warenkorb.map(item => (
                    <tr key={item.id}>
                      <td>
                        <div>
                          <Link to={`/artikel/${item.artikelId}`} className="text-decoration-none fw-bold">
                            {item.name}
                          </Link>
                        </div>
                      </td>
                      <td className="align-middle">
                        <div className="d-flex align-items-center justify-content-center">
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="me-2" 
                            onClick={() => updateMenge(item.id, Math.max(1, item.menge - 1))}
                            aria-label="Menge verringern"
                          >
                            <FaMinus />
                          </Button>
                          <Form.Control
                            type="number"
                            min="1"
                            value={item.menge}
                            onChange={(e) => updateMenge(item.id, Math.max(1, parseInt(e.target.value) || 1))}
                            style={{ width: '60px' }}
                            className="text-center"
                          />
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="ms-2" 
                            onClick={() => updateMenge(item.id, item.menge + 1)}
                            aria-label="Menge erhöhen"
                          >
                            <FaPlus />
                          </Button>
                        </div>
                      </td>
                      <td className="align-middle">
                        {item.kategorie || '-'}
                      </td>
                      <td className="align-middle">
                        {item.onlineshoplink ? (
                          <a href={item.onlineshoplink} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                            Zum Shop <FaExternalLinkAlt size={12} className="ms-1" />
                          </a>
                        ) : '-'}
                      </td>
                      <td className="align-middle text-end">
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => removeItem(item.id)}
                          aria-label="Artikel entfernen"
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-light">
                  <tr>
                    <td colSpan="4" className="text-end fw-bold">Gesamt {warenkorb.length} Artikel</td>
                    <td></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </Card.Body>
          <Card.Footer className="bg-white">
            <div className="d-flex flex-wrap justify-content-between align-items-center">
              <Button 
                variant="outline-danger" 
                className="mb-2 mb-md-0"
                onClick={clearWarenkorb}
                disabled={bestellungInProgress}
              >
                <FaTrash className="me-2" /> Warenkorb leeren
              </Button>
              <Button 
                variant="success" 
                onClick={createBestellung}
                disabled={bestellungInProgress}
              >
                {bestellungInProgress ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Bestellung wird erstellt...
                  </>
                ) : (
                  <>
                    <FaClipboardList className="me-2" /> Bestellung erstellen
                  </>
                )}
              </Button>
            </div>
          </Card.Footer>
        </Card>
      )}
    </Container>
  );
};

export default WarenkorbPage;
